import React, { useState } from 'react';
import { ArrowUpRightIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { usePortfolioData } from '../context/PortfolioDataContext';
import AnimatedSection from './AnimatedSection';
import SectionWithNeuralBg from './SectionWithNeuralBg';

const statusConfig = {
  'live': {
    label: 'Live',
    className: 'badge badge-green',
  },
  'in-development': {
    label: 'In Development',
    className: 'badge badge-blue',
  },
  'coming-soon': {
    label: 'Coming Soon',
    className: 'badge badge-yellow',
  },
};

export default function Products() {
  const { data } = usePortfolioData();
  const products = data.products;
  const [showAll, setShowAll] = useState(false);
  
  const featuredProducts = products.filter((product) => product.featured);
  const otherProducts = products.filter((product) => !product.featured);

  const renderProductCard = (product) => {
    const status = statusConfig[product.status] || statusConfig['live'];
    
    return (
      <AnimatedSection
        key={product.title}
        className="card group hover:border-primary-700 hover:shadow-xl hover:shadow-primary-900/20 transition-all duration-300">
        {/* Status Badge */}
        <div className="mb-4">
          <span className={status.className}>
            {status.label}
          </span>
        </div>

        {/* Title & Subtitle */}
        <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-gradient transition-all">
          {product.title}
        </h3>
        <p className="text-sm text-primary-400 mb-3 font-medium">{product.subtitle}</p>

        {/* Description */}
        <p className="text-gray-400 leading-relaxed mb-4 group-hover:text-gray-300 transition-colors">
          {product.description}
        </p>

        {/* Technologies */}
        {product.technologies && (
          <div className="flex flex-wrap gap-2 mb-4">
            {product.technologies.map((tech, idx) => (
              <span
                key={idx}
                className="text-xs px-3 py-1 bg-gray-700 text-gray-300 rounded-full border border-gray-600 group-hover:border-primary-600 group-hover:text-primary-400 transition-all">
                {tech}
              </span>
            ))}
          </div>
        )}

        {/* Link */}
        {product.link && (
          <a
            href={product.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-primary-400 hover:text-primary-300 font-medium transition-all group">
            View Project
            <ArrowUpRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        )}
      </AnimatedSection>
    );
  };

  const displayedProducts = showAll 
    ? [...featuredProducts, ...otherProducts]
    : featuredProducts;

  return (
    <SectionWithNeuralBg id="projects" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <AnimatedSection className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary-900/30 rounded-full flex items-center justify-center border border-primary-800/50">
              <RocketLaunchIcon className="w-8 h-8 text-primary-400" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            My Projects
          </h2>
          <p className="text-lg text-gray-400">
            A collection of projects showcasing full-stack development & AI
          </p>
        </AnimatedSection>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
          {displayedProducts.map(renderProductCard)}
        </div>

        {/* View All Button */}
        {!showAll && otherProducts.length > 0 && (
          <div className="text-center">
            <button
              onClick={() => setShowAll(true)}
              className="btn-secondary">
              View All Projects
            </button>
          </div>
        )}
      </div>
    </SectionWithNeuralBg>
  );
}
